import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

export function VsTable({
  before,
  after,
  beforeTitle = "Without Axy.digital",
  afterTitle = "With Axy.digital",
}: {
  before: string[];
  after: string[];
  beforeTitle?: string;
  afterTitle?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const rows = Math.max(before.length, after.length);

  return (
    <div ref={ref} className="grid md:grid-cols-2 gap-5">
      {/* Before */}
      <div className="glass-card p-6 md:p-7 opacity-90">
        <div className="flex items-center justify-between mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-white/50">{beforeTitle}</p>
          <span className="text-[10px] text-white/35">manual workflow</span>
        </div>
        <ul className="space-y-2.5">
          {Array.from({ length: rows }).map((_, i) => {
            const text = before[i];
            if (!text) return null;
            return (
              <li
                key={text}
                className="flex items-start gap-3 text-sm text-white/65"
                style={
                  seen
                    ? {
                        animation: `fade-in 400ms ease-out ${i * 90}ms both`,
                      }
                    : { opacity: 0 }
                }
              >
                <span className="grid place-items-center size-5 rounded-full bg-white/5 border border-white/10 mt-0.5 shrink-0">
                  <X className="size-3 text-white/45" />
                </span>
                <span className="line-through decoration-white/15">{text}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* After */}
      <div
        className="relative glass-card p-6 md:p-7 overflow-hidden"
        style={{
          boxShadow: "0 24px 80px rgba(217,70,239,0.25), 0 0 0 1px rgba(217,70,239,0.18) inset",
        }}
      >
        <div
          aria-hidden
          className="absolute -top-20 -right-12 size-60 rounded-full blur-3xl opacity-60"
          style={{
            background: "radial-gradient(circle, rgba(217,70,239,0.5), transparent 60%)",
          }}
        />
        <div className="relative flex items-center justify-between mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft">{afterTitle}</p>
          <span className="glass-pill px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/85 inline-flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />
            engine on
          </span>
        </div>
        <ul className="relative space-y-2.5">
          {Array.from({ length: rows }).map((_, i) => {
            const text = after[i];
            if (!text) return null;
            return (
              <li
                key={text}
                className="flex items-start gap-3 text-sm text-white/95"
                style={
                  seen
                    ? {
                        animation: `fade-in 500ms cubic-bezier(.2,.8,.2,1) ${i * 110 + 200}ms both`,
                      }
                    : { opacity: 0 }
                }
              >
                <span
                  className="grid place-items-center size-5 rounded-full mt-0.5 shrink-0 border border-white/15"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(244,114,208,0.9), rgba(123,63,242,0.9))",
                    boxShadow: "0 6px 18px rgba(217,70,239,0.45)",
                  }}
                >
                  <Check className="size-3 text-white" />
                </span>
                <span>{text}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
